/* eslint-disable @typescript-eslint/no-explicit-any */
import { useWindowSize } from "@/hooks";
import { PageType } from "@/services/shared-models";
import { useDataStore } from "@/stores";
import { Hit } from "algoliasearch";
import clsx from "clsx";
import { InfiniteHitsCache } from "instantsearch.js/es/connectors/infinite-hits/connectInfiniteHits";
import { BaseHit } from "instantsearch.js";
import { createInfiniteHitsSessionStorageCache } from "instantsearch.js/es/lib/infiniteHitsCache";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { Highlight, Snippet, useInfiniteHits, useSearchBox } from "react-instantsearch";
import { Link } from "react-router-dom";

const baseSessionStorageCache = createInfiniteHitsSessionStorageCache();

// A transient issue (backend indexing race, locale not resolved yet, network hiccup, ...) can make one
// search briefly return zero hits. The session cache is otherwise a good thing to keep (it restores scroll
// position on back-navigation), but caching a *zero-hit* response makes that one-off blip "sticky" - every
// identical search for the rest of the tab's session replays the cached empty result instead of re-querying,
// which is indistinguishable from search being broken. Only cache non-empty responses.
const sessionStorageCache: InfiniteHitsCache = {
    read: (args) => baseSessionStorageCache.read(args),
    write: (args) => {
        const hasHits = Object.values(args.hits).some((page) => page.length > 0);
        if (hasHits) baseSessionStorageCache.write(args);
    },
};

const Search = () => {
    const pages = useDataStore((state) => state.pages);

    const [width] = useWindowSize();
    const [left, setLeft] = useState(0);
    const sentinelRef = useRef<HTMLDivElement>(null);

    const { items, isLastPage, showMore } = useInfiniteHits({ cache: sessionStorageCache });
    const { query } = useSearchBox();

    const { t } = useTranslation("search");

    useLayoutEffect(() => {
        if (width === 2560) {
            setLeft(0);
        } else {
            const change = 2560 - width;
            setLeft(change * -0.5);
        }
    }, [width]);

    useEffect(() => {
        if (sentinelRef.current === null || typeof showMore !== "function") return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !isLastPage) {
                    showMore();
                }
            });
        });

        observer.observe(sentinelRef.current);

        return () => {
            observer.disconnect();
        };
    }, [isLastPage, showMore]);

    return (
        <>
            <Helmet>
                <title>
                    BIMids - {t("title", { ns: "search" })}: {query}
                </title>
            </Helmet>
            <div
                className={`absolute w-full h-full bg-primary-100 overflow-hidden`}
                style={{
                    left: `${left}px`,
                    width: 2015,
                    clipPath: "polygon(200px 0, 2015px 0, 838px 350px)",
                }}
            />

            <div className="container mx-auto 2xl:px-[108px]">
                <section id="hero" className="relative pt-[60px] mb-1">
                    <div className="mt-10">
                        <h1 className="flex content-center gap-3 items-center">{t("title", { ns: "search" })}</h1>
                    </div>
                </section>
                <main className="pb-[160px]">
                    <div className="relative grid grid-cols-[1fr] gap-13 gap-y-10 mt-36">
                        {items.map((hit: Hit<BaseHit>) => {
                            let type = "";
                            switch (hit.type) {
                                case 0:
                                    type = "element";
                                    break;
                                case 1:
                                    type = "content";
                                    break;
                                default:
                                    type = "unknown";
                            }

                            let slug = "";
                            if (type === "element") {
                                const basePage = pages.find((x) => x.pageType === PageType.Elements);
                                if (basePage) {
                                    slug = `../${basePage.slug}/${hit.slug}`;
                                }
                            } else if (type === "content") {
                                const page = pages.find((x) => x.slug === hit.slug);
                                if (page) {
                                    slug = `../${page.url}`;
                                } else {
                                    return null; // Skip rendering if page not found
                                }
                            }

                            return (
                                <Link key={hit.objectID} to={slug} className="no-underline">
                                    <div
                                        key={hit.objectID}
                                        className={clsx([
                                            "bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-300",
                                            type === "element" && "grid grid-cols-[1fr_300px]",
                                            type === "content" && "grid grid-cols-1",
                                        ])}
                                    >
                                        <div className="flex flex-col p-6">
                                            <h2 className="text-xl font-semibold mb-2">
                                                <Highlight attribute="title" hit={hit as any} />
                                            </h2>
                                            <p className="text-gray-700 mb-8">
                                                <Snippet attribute="data" hit={hit as any} />
                                            </p>
                                            <hr className="mt-auto my-4 border-gray-200" />
                                            <span className="text-sm text-gray-500">
                                                <span
                                                    className={clsx([
                                                        "inline-flex items-center rounded-md px-2 py-1 text-xs font-medium",
                                                        type === "element" && "bg-primary-100 text-primary-500",
                                                        type === "content" && "bg-yellow-100 text-yellow-800",
                                                    ])}
                                                >
                                                    {t(`result.type.${type}`, { ns: "search" })}
                                                </span>
                                            </span>
                                        </div>
                                        {type === "element" && (
                                            <div
                                                className="relative rounded-r-lg overflow-hidden bg-cover"
                                                style={{
                                                    width: 300,
                                                    minHeight: 300,
                                                    backgroundPosition: "top",
                                                    backgroundImage: `url(${hit.imageUrl})`,
                                                }}
                                            >
                                                <svg
                                                    viewBox="0 0 112 690"
                                                    fill="none"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    className="absolute bottom-0 top-[-50%] left-0 right-0 h-full text-white scale-200 origin-top-right"
                                                >
                                                    <path
                                                        d="M0 0h62.759v172C38.62 384 112 517 112 517v173H0V0z"
                                                        fill="currentColor"
                                                    />
                                                </svg>
                                            </div>
                                        )}
                                    </div>
                                </Link>
                            );
                        })}

                        <div ref={sentinelRef} aria-hidden="true" className="h-8" />

                        <div className="flex justify-center pb-10">
                            {isLastPage && (
                                <span className="text-gray-500 text-sm">{t("results-end", { ns: "search" })}</span>
                            )}
                        </div>
                    </div>
                </main>
            </div>
        </>
    );
};

export default Search;
